import { useState, useEffect, useCallback, useMemo } from "react";
import { DynamicDatePicker } from "../../../components/DatePicker";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import PaginationTable from "../../../components/Table";
import { RajasthanGenData } from "../../../models/rajasthan";
import {
  getDemoRajasthanGenerationData,
  getDemoRajasthanIsgsData,
  getRajasthanGenerationData,
  getRajasthanIsgsData,
  getRajReData,
} from "../../../Rest_api/RestApi";
import {
  formatDateForAPI,
  getLast7DaysDateRange,
  R_AREA_CHART_TYPES,
  R_CHART_TYPES,
} from "../const";
import {
  getBrushIndex,
  getRajIntervals,
  getShowBrush,
  handleKeyChange,
  handleUnitChange,
  INTERVAL,
  RAJ_UNITS,
} from "../const_raj";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import { titleCase } from "../../../extensions/extension";
import {
  getRandomColor,
  stringToConsistentColor,
} from "../../../extensions/string";
import { renderHourTick } from "../../../components/recharts/components";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { DataTabs, FilteredOutEntity } from "./Data";
import SecondaryHeader from "../Demand/SecondaryHeader";
import { useSelector } from "react-redux";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { FilterTab } from "../../../components/Filters";
import { ExchangeColors } from "../Exchange3/FormatData";
import { AddDatesWithFileName } from "../../../helpers/helper";

const GROUP_BY = [
  {
    label: "Interval",
    key: "",
    values: [],
  },
];
const CHART_TYPES = [R_CHART_TYPES[0], R_CHART_TYPES[2], R_AREA_CHART_TYPES];

const CONTENT_TYPE = ["Chart", "Table"];
const GENERATION_TYPE = ["Inter State", "Intra State"];

export function BySource() {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [data, setData] = useState<RajasthanGenData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedStation, setSelectedStation] = useState<any[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string[]>([]);
  const [selectedKey, setselectedKey] = useState(INTERVAL[0]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [selectedGenerationType, setSelectedGenerationType] =
    useState(GENERATION_TYPE);
  const [selectedContentType, setSelectedContentType] = useState(
    CONTENT_TYPE[0]
  );
  const [uniqueSource, setUniqueSource] = useState<string[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(RAJ_UNITS[0]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [uniqueStations, setUniqueStations] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedSource, setSelectedSource] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(CHART_TYPES[1]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState<any[]>([]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);
    const intraData = (
      await getDemoRajasthanGenerationData(startDateStr, endDateStr)
    )
      .filter(
        (item) => !FilteredOutEntity.includes(item.station + " Intrastate")
      )
      .map((item) => ({
        ...item,
        sector: "Intra State",
        source: titleCase(item.source),
        station: titleCase(item.source),
        month: getMonthFromDate(item.date),
      }))
      .filter(
        (item) =>
          item.backdown_mw !== 0 ||
          item.onBarDS_mw !== 0 ||
          item.onBarDc_mw !== 0
      );

    const reGenData = (
      await getRajReData({
        startDate: convertToString(dates.startDate),
        endDate: convertToString(dates.endDate),
      })
    )
      .map((item) => ({
        ...item,
        source: titleCase(item.energy_type),
        onBarDS_mw: item.value_mw,
        onBarDS_mwh: item.value_mw / 4,
        onBarDc_mw: 0,
        onBarDc_mwh: 0,
        backdown_mw: 0,
        backdown_mwh: 0,
        month: getMonthFromDate(item.date),
        station: titleCase(item.energy_type),
        sector: item.area === "InterState" ? "Inter State" : "Intra State",
        slot: item.time_block,
        state: "Demo State", // Assuming state is not available in this data
        // state: item.area,
      }))
      .filter((item) => item.area === "IntraState");
    const isgsData = (
      await getDemoRajasthanIsgsData({
        startDate: convertToString(dates.startDate),
        endDate: convertToString(dates.endDate),
      })
    ).map((item) => ({
      ...item,
      onBarDS_mw: item.value_mw,
      source: titleCase(item.from_gen__generation_source),
      onBarDS_mwh: item.value_mw / 4,
      onBarDc_mw: 0,
      onBarDc_mwh: 0,
      backdown_mw: 0,
      backdown_mwh: 0,
      station: titleCase(item.from_gen__generation_source),
      sector: "Inter State",
      month: getMonthFromDate(item.date),
    }));
    // .filter(
    //   (item) =>
    //     item.backdown_mw != 0 || item.onBarDS_mw != 0 || item.onBarDc_mw != 0
    // );

    const newData: RajasthanGenData[] = [
      ...reGenData,
      ...intraData,
      ...isgsData,
    ];

    setData(newData);
    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
        )
      )
    );
    setUniqueSource(Array.from(new Set(newData.map((item) => item.source))));
    setUniqueStations(Array.from(new Set(newData.map((item) => item.station))));
    setStates(Array.from(new Set(newData.map((item) => item.state))).sort());
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.slot))));
    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    let filteredData = data;
    if (selectedSource.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedSource.includes(item.source)
      );
    }
    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date)
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot)
      );
    }
    if (selectedGenerationType.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedGenerationType.includes(item.sector!)
      );
    }
    if (selectedState.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedState.includes(item.state)
      );
    }
    setUniqueStations(
      Array.from(new Set(filteredData.map((item) => item.station)))
    );

    if (selectedStation.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedStation.map((i) => i.label).includes(item.station)
      );
    }

    // filter by date
    const dates = DefaultDates[activeTab].defaultRangeDate;
    filteredData = filteredData.filter(
      (item) =>
        new Date(item.date) >= dates.startDate &&
        new Date(item.date) <= dates.endDate
    );

    const par: ChartDataParams = {
      keys: selectedKey.secondKey
        ? [selectedKey.firstKey, selectedKey.secondKey]
        : [selectedKey.firstKey],
      tableKeys: ["date", "slot"],
      groupBy: ["station"],
      volumeKey: "",
      aggregationKeys: [
        "onBarDc_mw",
        "backdown_mw",
        "offBarWarm_mw",
        "onBarDS_mwh",
        "onBarDc_mwh",
        "backdown_mwh",
        "offBarWarm_mwh",
      ],
      averageDuplicationKeys: ["date", "slot"],
      averageKeys: ["onBarDS_mw", "onBarDc_mw", "backdown_mw", "offBarWarm_mw"],
      sortby: selectedKey.firstKey == "station" ? ["_onBarDS_mw"] : undefined,
      reverseSort: ["date", "slot"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartKeys(cData.keys);
    setChartData(cData.data);
  }, [
    data,
    selectedGroupKeys,
    selectedStation,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedChartType,
    selectedKey,
    selectedState,
    selectedUnit,
    selectedSource,
    selectedGenerationType,
    DefaultDates,
    activeTab,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handlexDataKey = useCallback(() => {
    if (selectedKey.name === "Time Slot") {
      if (selectedBlock.length > 0) {
        return "slot";
      }
      return "slot";
    } else {
      return selectedKey.firstKey;
    }
  }, [selectedBlock.length, selectedKey.firstKey, selectedKey.name]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys =
    selectedKey.name === "Time Slot"
      ? [selectedKey.firstKey, selectedKey.secondKey].filter(
          (key): key is string => key !== undefined
        )
      : [selectedKey.firstKey];

  const summaryExtraLabels = selectedKey.secondKey
    ? [selectedKey.name, "Date"]
    : [selectedKey.name];

  const tablePar = {
    legends: getSelectedUnits()
      .map((item, index) => {
        return {
          dataKey: item.value + "_" + "onBarDS_" + selectedUnit.toLowerCase(),
          name: item.label,
          legendColor: undefined,
          stroke: stringToConsistentColor(item.label),
          type: selectedChartType.value,
          stackId: selectedChartType.stackId,
        };
      })
      .filter(Boolean),
    data: chartData,
    tableData: tableData, // Add this line to pass tableData to summary components
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Generation By Source",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Interval"
            items={getRajIntervals().map((item) => item.name)}
            selectedItems={[selectedKey.name]}
            onSelect={(item, index) => {
              handleKeyChange(index, setselectedKey, setSelectedUnit);
            }}
          />

          <FilterMenu
            title="Unit"
            items={RAJ_UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              handleUnitChange(item, selectedKey, setSelectedUnit);
            }}
          />
          <FilterMenu
            title="Source"
            items={uniqueSource}
            selectedItems={selectedSource}
            setSelectedItems={setSelectedSource}
          />
          <FilterMenu
            title="Area"
            items={GENERATION_TYPE}
            setSelectedItems={setSelectedGenerationType}
            selectedItems={selectedGenerationType}
          />

          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(CHART_TYPES[index]);
            }}
          />
          <FilterMenu
            title="State"
            items={states}
            selectedItems={selectedState}
            setSelectedItems={setSelectedState}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading">
            <h2 className="chartHeading w-80">Generation By Source</h2>
            <div className="flex gap-x-2 w-full justify-end">
              <SearchBox
                options={uniqueBlock.map((item) => {
                  return { value: item, label: item };
                })}
                isMany={true}
                placeholder="Search Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              />

              <SearchBox
                options={uniqueStations
                  .filter((item) => !item.includes("RE IntraState"))
                  .map((item) => {
                    return { value: item, label: item };
                  })}
                isMany={true}
                placeholder="Search Sources"
                selectedOptions={selectedStation}
                onChange={setSelectedStation}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  //

                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);

                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <>
              <ReMixChart
                data={chartData}
                xAxisTickMargin={10}
                showBrush={getShowBrush(chartData, selectedKey)}
                brushIndex={getBrushIndex(selectedKey)}
                isTimeSlot={selectedKey.name === "Time Slot"}
                onlyTitle={true}
                barCategoryGap={20}
                unit={selectedUnit}
                yAxisLabel={selectedUnit}
                legends={getSelectedUnits()
                  .map((item, index) => {
                    return {
                      dataKey:
                        item.value +
                        "_" +
                        "onBarDS_" +
                        selectedUnit.toLowerCase(),
                      name: item.label,
                      legendColor: undefined,
                      stroke: ExchangeColors[index],
                      type: selectedChartType.value,
                      stackId: selectedChartType.stackId,
                    };
                  })
                  .filter(Boolean)}
                xDataKey={xDataKey}
                xTick={
                  selectedKey.secondKey !== undefined &&
                  selectedBlock.length == 0
                    ? (value: any, index: any) => {
                        return renderHourTick(value, 10);
                      }
                    : undefined
                }
                secondXDataKey={
                  //   "date"
                  selectedKey.secondKey
                }
              />
            </>
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5 ">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );

  function getSelectedUnits(): {
    value: string;
    label: string;
    color: string;
    type: string;
  }[] {
    let keys = chartKeys.map((item) => {
      console.log("====ITEM====");
      console.log("_" + item.value + "_" + selectedUnit.toLowerCase());
      return {
        ...item,
        value: item,
        label: item,
        color: getRandomColor(item),
        type: "onLoad",
      };
    });

    return keys;
  }
}
